import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import { ROAD_SOUNDS } from '../../logic/road/sounds';
import { glowTexture, prefersReducedMotion } from './glow';
import { TitlePlaque } from '../board/index';

/** The moment's beats, in ms. */
export const MOMENT = {
  /** The ruin lifts off the road to the middle of the screen. */
  lift: 450,
  /** The coins have flown in from the wallet; it bursts into its restored painting. */
  burst: 1150,
  /** It flies home to the road. */
  home: 3000,
  /** All over. */
  end: 3500,
} as const;

/** Reduced motion: the restored landmark and its name, then gone. */
const QUICK_MS = 1600;

const easeOut = (t: number) => 1 - (1 - t) ** 3;
const easeIn = (t: number) => t ** 3;
const backOut = (t: number) => 1 + 2.4 * (t - 1) ** 3 + 1.4 * (t - 1) ** 2;
const clamp = (t: number) => Math.max(0, Math.min(1, t));

let raysTex: Texture | null = null;
/** Soft gold rays fading out from the centre, drawn once. */
function raysTexture(): Texture {
  if (raysTex) return raysTex;
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const c = size / 2;
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.arc(c, c, c, a, a + Math.PI / 20);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255,228,138,0.42)';
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'destination-in';
    const g = ctx.createRadialGradient(c, c, 0, c, c, c);
    g.addColorStop(0, 'rgba(0,0,0,1)');
    g.addColorStop(0.35, 'rgba(0,0,0,1)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  raysTex = Texture.from(canvas);
  return raysTex;
}

/** A four-point sparkle. */
function sparkle(size: number, color: number): Graphics {
  const s = size / 2;
  const k = s * 0.24;
  return new Graphics().poly([0, -s, k, -k, s, 0, k, k, 0, s, -k, k, -s, 0, -k, -k]).fill(color);
}

export interface MomentSpot {
  x: number;
  y: number;
  size: number;
}

/**
 * A landmark's restore, played like TV over the slot: the ruin lifts off the
 * road into the middle of the screen, the coins fly into it from the wallet,
 * it bursts into its restored painting under turning rays with a fanfare
 * and its name on a plaque, then flies home to its place on the road. With
 * reduced motion it shows the restored landmark and its name for a moment.
 * Driven by {@link tick}; {@link done} resolves at the end.
 */
export class RestoreMoment extends Container {
  readonly done: Promise<void>;
  private resolveDone!: () => void;
  private readonly dim = new Graphics();
  private readonly rays = new Sprite(raysTexture());
  private readonly glow = new Sprite(glowTexture());
  private readonly sparks: { g: Graphics; dx: number; dy: number; delay: number; life: number }[] =
    [];
  private readonly piece = new Container();
  private readonly ruined: Sprite;
  private readonly restored: Sprite;
  private readonly plaque: TitlePlaque;
  private readonly coins: { s: Sprite; delay: number; landed: boolean }[] = [];
  private readonly reduced = prefersReducedMotion();
  private t = 0;
  private burst = false;
  private finished = false;
  private stage = { x: 0, y: 0, size: 200 };

  constructor(
    private readonly opts: {
      name: string;
      ruined: Texture | null;
      restored: Texture | null;
      coin: Texture | null;
      /** Where the landmark stands on the road (its square). */
      from: MomentSpot;
      /** Where it stands once restored, when that differs. */
      to?: MomentSpot | null;
      /** The wallet's coin counter. */
      wallet: { x: number; y: number } | null;
      width: number;
      height: number;
    },
  ) {
    super();
    this.done = new Promise((r) => (this.resolveDone = r));
    // Nothing behind it takes taps meanwhile.
    this.dim.eventMode = 'static';
    const { width: w, height: h } = opts;
    const size = Math.round(Math.min(w * 0.56, h * 0.34, 260));
    this.stage = { x: w / 2, y: h * 0.44, size };

    this.dim.rect(0, 0, w, h).fill({ color: 0x0a0602, alpha: 0.7 });
    this.dim.alpha = 0;
    this.rays.anchor.set(0.5);
    this.rays.position.set(this.stage.x, this.stage.y);
    this.rays.width = this.rays.height = size * 2.6;
    this.rays.visible = false;
    this.glow.anchor.set(0.5);
    this.glow.tint = 0xfff0b4;
    this.glow.blendMode = 'add';
    this.glow.position.set(this.stage.x, this.stage.y);
    this.glow.visible = false;
    for (let k = 0; k < 16; k++) {
      const angle = (k / 16) * Math.PI * 2 + 0.2;
      const reach = size * (0.62 + (k % 3) * 0.14);
      const g = sparkle(18, k % 2 ? 0xffe48a : 0xfff6d0);
      g.position.set(this.stage.x, this.stage.y);
      g.visible = false;
      this.sparks.push({
        g,
        dx: Math.cos(angle) * reach,
        dy: Math.sin(angle) * reach,
        delay: k * 15,
        life: 750 + (k % 4) * 100,
      });
    }

    this.ruined = new Sprite(opts.ruined ?? Texture.EMPTY);
    this.restored = new Sprite(opts.restored ?? Texture.EMPTY);
    for (const s of [this.ruined, this.restored]) {
      s.anchor.set(0.5);
      const tex = s.texture;
      s.scale.set(tex === Texture.EMPTY ? 1 : 1 / Math.max(tex.width, tex.height));
    }
    this.restored.visible = false;
    this.piece.addChild(this.ruined, this.restored);

    this.plaque = new TitlePlaque(
      `${opts.name} restored!`,
      Math.max(20, Math.min(28, Math.round(w / 16))),
    );
    this.plaque.position.set(this.stage.x, Math.min(h - 50, this.stage.y + size * 0.58));
    this.plaque.visible = false;
    if (this.plaque.width > w - 24) this.plaque.scale.set((w - 24) / this.plaque.width);

    if (opts.wallet && opts.coin && !this.reduced) {
      for (let k = 0; k < 6; k++) {
        const s = new Sprite(opts.coin);
        s.anchor.set(0.5);
        s.width = s.height = 28;
        s.visible = false;
        this.coins.push({ s, delay: k * 40, landed: false });
      }
    }

    this.addChild(
      this.dim,
      this.rays,
      this.glow,
      ...this.sparks.map((s) => s.g),
      this.piece,
      this.plaque,
      ...this.coins.map((c) => c.s),
    );
    this.place(this.reduced ? this.stage : this.road());
    if (this.reduced) this.reveal();
  }

  /** The landmark's square on the road, as a centre and size: before, or once home. */
  private road(home = false) {
    const f = (home && this.opts.to) || this.opts.from;
    return { x: f.x + f.size / 2, y: f.y + f.size / 2, size: f.size };
  }

  private place(p: { x: number; y: number; size: number }): void {
    this.piece.position.set(p.x, p.y);
    this.piece.scale.set(p.size);
  }

  private reveal(): void {
    this.burst = true;
    this.ruined.visible = !this.reduced;
    this.restored.visible = true;
    this.plaque.visible = true;
    AudioBus.shared.play(ROAD_SOUNDS.restored, { volume: 0.85 });
    if (this.reduced) {
      this.dim.alpha = 1;
      return;
    }
    this.rays.visible = true;
    this.glow.visible = true;
    for (const s of this.sparks) s.g.visible = true;
  }

  tick(deltaMs: number): void {
    if (this.finished) return;
    this.t += deltaMs;
    const t = this.t;
    if (this.reduced) {
      if (t >= QUICK_MS) this.finish();
      return;
    }
    const road = this.road();
    // The dim fades in, and out as the landmark flies home.
    this.dim.alpha =
      t < MOMENT.home ? clamp(t / 300) : 1 - clamp((t - MOMENT.home) / (MOMENT.end - MOMENT.home));

    if (t < MOMENT.lift) {
      const k = easeOut(t / MOMENT.lift);
      this.place({
        x: road.x + (this.stage.x - road.x) * k,
        y: road.y + (this.stage.y - road.y) * k,
        size: road.size + (this.stage.size - road.size) * k,
      });
    } else if (t < MOMENT.home) {
      // On the stage: a little bump as each coin lands, then the burst.
      let bump = 0;
      if (t < MOMENT.burst) {
        const since = (t - MOMENT.lift) % 110;
        bump = 0.05 * Math.sin((since / 110) * Math.PI);
      }
      this.place({ ...this.stage, size: this.stage.size * (1 + bump) });
    } else {
      const k = easeIn(clamp((t - MOMENT.home) / (MOMENT.end - MOMENT.home)));
      const home = this.road(true);
      this.place({
        x: this.stage.x + (home.x - this.stage.x) * k,
        y: this.stage.y + (home.y - this.stage.y) * k,
        size: this.stage.size + (home.size - this.stage.size) * k,
      });
    }

    // Coins from the wallet into the landmark.
    const wallet = this.opts.wallet;
    for (const c of this.coins) {
      const k = (t - MOMENT.lift - c.delay) / 480;
      c.s.visible = k > 0 && k < 1;
      if (!c.s.visible || !wallet) continue;
      const e = easeOut(k);
      const mx = (wallet.x + this.stage.x) / 2 + 40;
      const my = (wallet.y + this.stage.y) / 2 - 30;
      // A gentle arc through (mx, my).
      const u = 1 - e;
      c.s.position.set(
        u * u * wallet.x + 2 * u * e * mx + e * e * this.stage.x,
        u * u * wallet.y + 2 * u * e * my + e * e * this.stage.y,
      );
      c.s.alpha = Math.min(1, k * 8);
      if (k > 0.95 && !c.landed) {
        // A clink for every other coin, so six don't pile up into noise.
        c.landed = true;
        const n = this.coins.indexOf(c);
        const coins = ROAD_SOUNDS.coins;
        if (n % 2 === 0)
          AudioBus.shared.play(coins[(n / 2) % coins.length] ?? coins[0], { volume: 0.5 });
      }
    }

    if (!this.burst && t >= MOMENT.burst) this.reveal();
    if (this.burst) {
      const b = t - MOMENT.burst;
      // Ruin out, restored in with an overshoot.
      this.ruined.alpha = 1 - clamp(b / 300);
      const inK = clamp(b / 650);
      this.restored.alpha = clamp(b / 200);
      this.restored.scale.set(
        (backOut(inK) * 0.5 + 0.5) /
          Math.max(this.restored.texture.width, this.restored.texture.height, 1),
      );
      this.rays.rotation = (b / 1000) * 0.4;
      this.rays.alpha = (t < MOMENT.home ? clamp(b / 280) : this.dim.alpha) * 0.95;
      const rk = easeOut(clamp(b / 900));
      this.rays.scale.set(((0.4 + 0.6 * rk) * this.stage.size * 2.6) / this.rays.texture.width);
      const gk = clamp(b / 900);
      this.glow.alpha = 0.95 * (1 - gk);
      this.glow.width = this.glow.height = this.stage.size * 1.3 * (0.3 + 1.5 * gk);
      for (const s of this.sparks) {
        const k = clamp((b - s.delay) / s.life);
        s.g.position.set(this.stage.x + s.dx * easeOut(k), this.stage.y + s.dy * easeOut(k));
        s.g.alpha = 1 - k;
        s.g.scale.set(0.5 + 0.6 * k);
      }
      const pk = clamp(b / 420);
      this.plaque.alpha = t < MOMENT.home ? pk : this.dim.alpha;
    }
    if (t >= MOMENT.end) this.finish();
  }

  private finish(): void {
    this.finished = true;
    this.resolveDone();
  }
}
